import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Product } from '../../../../types/Products/Products';
import useProducts from '../../../../hooks/useProducts';
import { NavForward } from '../../../../components/buttons/NavForward';
import { SelectedProduct } from '../../../../components/products/SelectedProduct';
import { Button } from '../../../../components';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';

export const Route = createFileRoute('/_authenticated/$patientId/payment/confirmation')({
  component: ProductConfirmationPage,
});

function ProductConfirmationPage() {
  const [selected, setSelected] = React.useState<string>('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [productConfirmed, setProductConfirmed] = React.useState<boolean>(false);
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const productsDocQuery = useProducts();
  const productsDocData = productsDocQuery.data as Product[];

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (productsDocData && chart) {
      let productFullName = '';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          productFullName = 'Essential Package';
          setSelected('Essential Package');
          break;
        case 'vital':
          productFullName = 'Vital Package';
          setSelected('Vital Package');
          break;
        case 'vip':
          productFullName = 'Physician Guided Package';
          setSelected('Physician Guided Package');
          break;
        default:
          break;
      }
      setSelectedProduct(productsDocData.find((product) => product.name === productFullName));
    }
  }, [productsDocData, chart]);

  const handleConfirm = () => {
    setProductConfirmed(true);
  };

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'confirmation', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'confirmation',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  if (chartQuery.isLoading || patientDocQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && productsDocData?.length > 0) {
    return (
      <>
        <div id="medicare-abn-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Product Confirmation" />
          <div id="product-cards-container" className="flex w-10/12 flex-row flex-wrap justify-around">
            {selectedProduct && <SelectedProduct product={selectedProduct} selected={selected} />}
          </div>
          <Button type="button" id="confirm-product" onClick={handleConfirm} textContent="Confirm Product Selection" />
          <div id="nav" className="flex flex-row">
            {/* {prevPage && <NavBack to={prevPage} />} */}
            {nextPageURL && <NavForward to={nextPageURL} disabled={!productConfirmed} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
